import React, { useState } from "react";
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const MaduracionSection = ({
  maduracionRecords = [],
  destilacionRecords = [],
  granelRecords = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveMaduracion
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const today = new Date().toISOString().split('T')[0];

  // Evaluador NOM-070 para determinar la clase y edad según recipiente y días transcurridos
  const evaluarNormaClase = (tipoRecipiente, fechaInicio, fechaFin) => {
    if (!fechaInicio) return { clase: "Sin definir", edad: "0 días" };

    const inicio = new Date(fechaInicio);
    const fin = fechaFin ? new Date(fechaFin) : new Date();
    const diffTime = fin.getTime() - inicio.getTime();

    if (isNaN(diffTime) || diffTime < 0) {
      return { clase: "Sin definir", edad: "0 días" };
    }

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const months = Math.floor(diffDays / 30.4375);
    const years = Math.floor(diffDays / 365.25);

    const isMadera = (tipoRecipiente || "").toUpperCase().includes("MADERA") ||
      (tipoRecipiente || "").toUpperCase().includes("BARRICA") ||
      (tipoRecipiente || "").toUpperCase().includes("PIPÓN") ||
      (tipoRecipiente || "").toUpperCase().includes("TONEL");

    if (isMadera) {
      if (diffDays >= 365) {
        const remMonths = months % 12;
        return {
          clase: "Añejo",
          edad: `${years} año(s) ${remMonths > 0 ? remMonths + ' mes(es)' : ''}`.trim()
        };
      } else if (diffDays >= 60) {
        const remDays = diffDays % 30;
        return {
          clase: "Reposado",
          edad: `${months} mes(es) ${remDays > 0 ? remDays + ' día(s)' : ''}`.trim()
        };
      } else {
        return {
          clase: "En Maduración (Madera)",
          edad: `${diffDays} día(s)`
        };
      }
    } else {
      // Recipiente de Vidrio
      if (diffDays >= 365) {
        const remMonths = months % 12;
        return {
          clase: "Madurado en Vidrio",
          edad: `${years} año(s) ${remMonths > 0 ? remMonths + ' mes(es)' : ''}`.trim()
        };
      } else {
        return {
          clase: "En Maduración (Vidrio)",
          edad: `${months > 0 ? months + ' mes(es) ' : ''}${diffDays % 30} día(s)`
        };
      }
    }
  };

  // Obtenemos o inicializamos los recipientes
  const recipientes = formData?.recipientes && formData.recipientes.length > 0
    ? formData.recipientes
    : [{
      id: Date.now(),
      tipoRecipiente: "BARRICAS DE MADERA DE",
      detalleRecipiente: "",
      noRecipientes: "1",
      invInicialFecha: formData?.fechaMovimiento || today,
      invInicialRecipientes: "1",
      invInicialVolumen: formData?.invInicialVolumen || "",
      invInicialAlc: formData?.invInicialAlc || "",
      entradaFolio: "",
      entradaRecipientes: "0",
      entradaVolumen: "0",
      entradaAlc: "0",
      salidaFecha: today,
      salidaRecipientes: "0",
      salidaVolumen: "0",
      salidaAlc: "0",
      salidaMerma: "0",
      invFinalRecipientes: "1",
      invFinalVolumen: "",
      invFinalAlc: "",
      claseObtenida: "",
      edadObtenida: ""
    }];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Selección Inteligente de Lote Origen
  const handleLoteSelect = (e) => {
    const selectedKey = e.target.value;
    if (!selectedKey) {
      setFormData(prev => ({
        ...prev,
        noLote: "",
        granelOrigenId: null,
        granelVolDisponible: 0
      }));
      return;
    }

    let isGranel = selectedKey.startsWith("GRANEL:");
    let isDestilacion = selectedKey.startsWith("DESTILACION:");
    const rawId = selectedKey.replace("GRANEL:", "").replace("DESTILACION:", "");

    if (isGranel || (!isDestilacion && granelRecords.some(g => (g.noLote || g.idContenedor || g.id?.toString()) === rawId))) {
      const gRec = granelRecords.find(g => (g.noLote || g.idContenedor || g.id?.toString()) === rawId);
      if (gRec) {
        const entVol = parseFloat(gRec.entradaVolumen || gRec.invInicialVol || 0);
        const salVol = parseFloat(gRec.salidaVolumen || 0);
        const volDisp = gRec.invFinalVol !== undefined && gRec.invFinalVol !== ""
          ? parseFloat(gRec.invFinalVol)
          : Math.max(0, entVol - salVol);
        const alc = gRec.invFinalAlc || gRec.entradaAlc || gRec.invInicialAlc || "";

        setFormData(prev => ({
          ...prev,
          noLote: gRec.noLote || gRec.idContenedor || rawId,
          origenTipo: "granel",
          granelOrigenId: gRec.id,
          granelVolDisponible: volDisp,
          granelAlcGrad: alc,
          tipoMaguey: gRec.tipoMaguey || prev.tipoMaguey || "",
          categoria: gRec.categoria || prev.categoria || "Mezcal",
          ingredientes: gRec.ingredientes || prev.ingredientes || "",
          noAnalisis: gRec.noAnalisis || prev.noAnalisis || "",
          clase: gRec.clase || "Joven / Blanco"
        }));
      }
    } else {
      const destRecord = destilacionRecords.find(r => r.noProduccion === rawId || r.id?.toString() === rawId);
      if (destRecord) {
        let ingredienteArray = [];
        if (destRecord.items && Array.isArray(destRecord.items)) {
          destRecord.items.forEach(it => {
            if (it.destiladoConIngrediente && it.destiladoConIngrediente.trim() !== '') {
              ingredienteArray.push(it.destiladoConIngrediente.trim());
            } else if (it.destiladoConIngredientes && Array.isArray(it.destiladoConIngredientes)) {
              ingredienteArray.push(...it.destiladoConIngredientes);
            }
          });
        }
        const ingredientesUnicos = [...new Set(ingredienteArray.filter(Boolean))].join(', ');

        setFormData(prev => ({
          ...prev,
          noLote: rawId,
          origenTipo: "destilacion",
          granelOrigenId: null,
          granelVolDisponible: 0,
          ingredientes: ingredientesUnicos || prev.ingredientes || "",
          tipoMaguey: destRecord.tipoMaguey || prev.tipoMaguey || "",
          categoria: destRecord.categoria || prev.categoria || "Mezcal",
          noAnalisis: destRecord.noAnalisis || destRecord.noInforme || prev.noAnalisis || ""
        }));
      } else {
        setFormData(prev => ({ ...prev, noLote: rawId }));
      }
    }
  };

  const handleAddRecipiente = () => {
    const newRec = {
      id: Date.now(),
      tipoRecipiente: "BARRICAS DE MADERA DE",
      detalleRecipiente: "",
      noRecipientes: "1",
      invInicialFecha: formData?.fechaMovimiento || today,
      invInicialRecipientes: "1",
      invInicialVolumen: "",
      invInicialAlc: formData?.granelAlcGrad || "",
      entradaFolio: "",
      entradaRecipientes: "0",
      entradaVolumen: "0",
      entradaAlc: "0",
      salidaFecha: today,
      salidaRecipientes: "0",
      salidaVolumen: "0",
      salidaAlc: "0",
      salidaMerma: "0",
      invFinalRecipientes: "1",
      invFinalVolumen: "",
      invFinalAlc: "",
      claseObtenida: "",
      edadObtenida: ""
    };
    setFormData(prev => ({
      ...prev,
      recipientes: [...recipientes, newRec]
    }));
  };

  const handleRemoveRecipiente = (id) => {
    if (recipientes.length === 1) {
      alert("Debe incluir al menos un recipiente en el proceso de maduración.");
      return;
    }
    setFormData(prev => ({
      ...prev,
      recipientes: recipientes.filter(r => r.id !== id)
    }));
  };

  const handleRecipienteChange = (id, field, value) => {
    setFormData(prev => {
      const updatedRecs = (prev.recipientes || recipientes).map(rec => {
        if (rec.id !== id) return rec;

        const updated = { ...rec, [field]: value };

        const fechaInicio = updated.invInicialFecha || prev.fechaMovimiento || today;
        const fechaFin = updated.salidaFecha || today;
        const normaResult = evaluarNormaClase(updated.tipoRecipiente, fechaInicio, fechaFin);

        updated.claseObtenida = normaResult.clase;
        updated.edadObtenida = normaResult.edad;

        const vIni = parseFloat(updated.invInicialVolumen || 0);
        const vEnt = parseFloat(updated.entradaVolumen || 0);
        const vSal = parseFloat(updated.salidaVolumen || 0);
        const vMer = parseFloat(updated.salidaMerma || 0);

        if (!updated.invFinalVolumen || field === 'invInicialVolumen' || field === 'entradaVolumen' || field === 'salidaVolumen' || field === 'salidaMerma') {
          const saldoFinal = Math.max(0, (vIni + vEnt) - (vSal + vMer));
          updated.invFinalVolumen = saldoFinal > 0 ? saldoFinal.toString() : "0";
        }

        return updated;
      });

      return { ...prev, recipientes: updatedRecs };
    });
  };

  const handleEditRecord = (rec) => {
    if (rec.status !== "borrador" && rec.status !== "rechazado") {
      alert("Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.");
      return;
    }
    setFormData({
      fechaMovimiento: rec.fechaMovimiento || today,
      noLote: rec.noLote || "",
      categoria: rec.categoria || "Mezcal",
      clase: rec.clase || "Joven / Blanco",
      ingredientes: rec.ingredientes || "",
      edad: rec.edad || "",
      tipoMaguey: rec.tipoMaguey || "",
      noAnalisis: rec.noAnalisis || "",
      recipientes: rec.recipientes && rec.recipientes.length > 0 ? rec.recipientes : [{
        id: Date.now(),
        tipoRecipiente: rec.tipoRecipiente || "BARRICAS DE MADERA DE",
        detalleRecipiente: rec.detalleRecipiente || "",
        noRecipientes: rec.invInicialRecipientes || "1",
        invInicialFecha: rec.invInicialFecha || rec.fechaMovimiento || today,
        invInicialRecipientes: rec.invInicialRecipientes || "1",
        invInicialVolumen: rec.invInicialVolumen || "",
        invInicialAlc: rec.invInicialAlc || "",
        entradaFolio: rec.entradaFolio || "",
        entradaRecipientes: rec.entradaRecipientes || "0",
        entradaVolumen: rec.entradaVolumen || "0",
        entradaAlc: rec.entradaAlc || "0",
        salidaFecha: rec.salidaFecha || today,
        salidaRecipientes: rec.salidaRecipientes || "0",
        salidaVolumen: rec.salidaVolumen || "0",
        salidaAlc: rec.salidaAlc || "0",
        salidaMerma: rec.salidaMerma || "0",
        invFinalRecipientes: rec.invFinalRecipientes || "1",
        invFinalVolumen: rec.invFinalVolumen || "",
        invFinalAlc: rec.invFinalAlc || "",
        claseObtenida: rec.salidaClaseObtenida || "",
        edadObtenida: rec.salidaEdadObtenida || ""
      }],
      observaciones: rec.observaciones || ""
    });
    setIsFormOpen(true);
  };

  const totalVolumenMadurar = recipientes.reduce((sum, r) => sum + (parseFloat(r.invInicialVolumen || 0) + parseFloat(r.entradaVolumen || 0)), 0);
  const superaSaldoGranel = formData.granelVolDisponible > 0 && totalVolumenMadurar > formData.granelVolDisponible;

  if (!isFormOpen) {
    return (
      <div className="animate-fadeIn">
        <div className="history-section-header" style={{ marginBottom: "20px" }}>
          <div>
            <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--agave-green)" }}>
              Historial de Registro de Maduración (Sección VII)
            </h2>
            <p style={{ margin: "4px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
              Datos Generales, V.Bo. de Revisor y Acciones de Control
            </p>
          </div>

          <button
            onClick={() => {
              setFormData({
                fechaMovimiento: today,
                noLote: "",
                categoria: "Mezcal",
                clase: "Joven / Blanco",
                recipientes: [{
                  id: Date.now(),
                  tipoRecipiente: "BARRICAS DE MADERA DE",
                  detalleRecipiente: "",
                  noRecipientes: "1",
                  invInicialFecha: today,
                  invInicialRecipientes: "1",
                  invInicialVolumen: "",
                  invInicialAlc: "",
                  entradaFolio: "",
                  entradaRecipientes: "0",
                  entradaVolumen: "0",
                  entradaAlc: "0",
                  salidaFecha: today,
                  salidaRecipientes: "0",
                  salidaVolumen: "0",
                  salidaAlc: "0",
                  salidaMerma: "0",
                  invFinalRecipientes: "1",
                  invFinalVolumen: "",
                  invFinalAlc: "",
                  claseObtenida: "Reposado",
                  edadObtenida: ""
                }]
              });
              setIsFormOpen(true);
            }}
            className="btn-add-new"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px" }}
          >
            <AddIcon style={{ fontSize: 20 }} />
            <span style={{ fontWeight: 700 }}>Nuevo Registro de Maduración</span>
          </button>
        </div>

        <div className="records-list-wrapper" style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
          <table className="records-table">
            <thead>
              <tr>
                <th>Fecha Mov.</th>
                <th>Nº Lote</th>
                <th>Categoría / Clase Origen</th>
                <th>Tipo de Maguey</th>
                <th>Nº Análisis</th>
                <th>Revisor (V.Bo. Inspector)</th>
                <th>Estado</th>
                <th style={{ textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {maduracionRecords.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                    <InventoryIcon style={{ fontSize: 40, opacity: 0.4, marginBottom: "8px" }} />
                    <p style={{ margin: 0, fontSize: "14px" }}>No hay registros de maduración almacenados en el sistema.</p>
                  </td>
                </tr>
              ) : (
                maduracionRecords.map((rec, i) => (
                  <tr key={i}>
                    <td>{rec.fechaMovimiento}</td>
                    <td style={{ fontWeight: 800, color: "var(--agave-green)" }}>{rec.noLote}</td>
                    <td>
                      <strong>{rec.categoria}</strong> <br />
                      <small style={{ color: "var(--text-muted)" }}>{rec.clase}</small>
                    </td>
                    <td>{rec.tipoMaguey || "-"}</td>
                    <td>{rec.noAnalisis || "-"}</td>
                    <td style={{ fontSize: "12px", fontWeight: 600 }}>{rec.inspector || "Pendiente V.Bo."}</td>
                    <td>
                      <span className={`badge-status ${rec.status}`}>
                        {rec.status === "aprobado" ? "Aprobado" : rec.status === "revision" ? "En revisión" : "Borrador"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver Detalle Completo"
                          style={{ background: "rgba(52, 152, 219, 0.15)", color: "#3498db", border: "1px solid rgba(52, 152, 219, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleEditRecord(rec)}
                          disabled={rec.status !== "borrador" && rec.status !== "rechazado"}
                          title={rec.status === "borrador" || rec.status === "rechazado" ? "Editar Registro" : "Solo editables en estado Borrador"}
                          style={{
                            background: rec.status === "borrador" || rec.status === "rechazado" ? "rgba(241, 196, 15, 0.15)" : "rgba(255,255,255,0.04)",
                            color: rec.status === "borrador" || rec.status === "rechazado" ? "#f1c40f" : "var(--text-muted)",
                            border: rec.status === "borrador" || rec.status === "rechazado" ? "1px solid rgba(241, 196, 15, 0.3)" : "1px solid rgba(255,255,255,0.08)",
                            borderRadius: "6px",
                            padding: "5px 8px",
                            cursor: rec.status === "borrador" || rec.status === "rechazado" ? "pointer" : "not-allowed",
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* MODAL DETALLE COMPLETO */}
        {selectedRecord && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: "20px" }}>
            <div className="detail-card animate-fadeIn" style={{ maxWidth: "800px", width: "100%", maxHeight: "90vh", overflowY: "auto", background: "#161b22", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "16px", padding: "24px", position: "relative", boxShadow: "0 20px 40px rgba(0,0,0,0.5)" }}>
              <button
                onClick={() => setSelectedRecord(null)}
                style={{ position: "absolute", top: "16px", right: "16px", background: "rgba(255,255,255,0.05)", border: "none", borderRadius: "50%", padding: "6px", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <CloseIcon />
              </button>

              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px", marginBottom: "16px" }}>
                <span className={`badge-status ${selectedRecord.status}`} style={{ float: "right", marginTop: "4px" }}>
                  {selectedRecord.status === "aprobado" ? "Aprobado" : selectedRecord.status === "revision" ? "En revisión" : "Borrador"}
                </span>
                <h3 style={{ margin: 0, color: "var(--agave-green)", fontSize: "20px", fontWeight: 800 }}>
                  Detalle Lote Maduración: {selectedRecord.noLote}
                </h3>
                <small style={{ color: "var(--text-muted)", fontSize: "12px" }}>Sección VII - Proceso de Maduración (NOM-070-SCFI-2016)</small>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "14px", marginBottom: "20px", background: "rgba(255,255,255,0.02)", padding: "14px", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.05)" }}>
                <div><strong>Fecha Movimiento:</strong> {selectedRecord.fechaMovimiento}</div>
                <div><strong>Categoría / Clase Origen:</strong> {selectedRecord.categoria} / {selectedRecord.clase}</div>
                <div><strong>Ingredientes:</strong> {selectedRecord.ingredientes || "Ninguno"}</div>
                <div><strong>Tipo Maguey:</strong> {selectedRecord.tipoMaguey || "-"}</div>
                <div><strong>Nº Análisis Fisicoquímico:</strong> {selectedRecord.noAnalisis || "-"}</div>
                <div><strong>Revisor (Inspector V.Bo.):</strong> <span style={{ color: "#f1c40f", fontWeight: 700 }}>{selectedRecord.inspector || "Pendiente V.Bo."}</span></div>
              </div>

              <h4 style={{ color: "var(--copper)", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "8px", marginBottom: "12px", display: "flex", alignItems: "center", gap: 8 }}>
                <ScienceIcon style={{ fontSize: 20 }} />
                <span>Recipientes de Maduración ({selectedRecord.recipientes?.length || 1})</span>
              </h4>

              {(selectedRecord.recipientes || [selectedRecord]).map((r, idx) => (
                <div key={idx} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "14px", marginBottom: "12px", fontSize: "13px" }}>
                  <div style={{ fontWeight: 800, color: "var(--agave-green)", marginBottom: "8px", fontSize: "14px" }}>
                    📦 Recipiente #{idx + 1}: {r.tipoRecipiente} ({r.detalleRecipiente || 'Sin detalle'})
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
                    <div><strong>Ingreso:</strong> {r.invInicialFecha} | {r.invInicialVolumen} L ({r.invInicialAlc}%)</div>
                    <div><strong>Entradas:</strong> {r.entradaVolumen} L (Folio: {r.entradaFolio || '-'})</div>
                    <div><strong>Salidas:</strong> {r.salidaVolumen} L | Merma: {r.salidaMerma} L</div>
                    <div><strong>Clase Obtenida (NOM-070):</strong> <span style={{ color: "#2ecc71", fontWeight: 800 }}>{r.claseObtenida || r.salidaClaseObtenida || 'En Maduración'}</span></div>
                    <div><strong>Tiempo / Edad:</strong> {r.edadObtenida || r.salidaEdadObtenida || '-'}</div>
                    <div><strong>Saldo Final:</strong> <span style={{ color: "var(--agave-green)", fontWeight: 800 }}>{r.invFinalVolumen} L</span></div>
                  </div>
                </div>
              ))}

              <div style={{ marginTop: "16px", paddingTop: "12px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <strong>Observaciones:</strong>
                <p style={{ margin: "6px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>{selectedRecord.observaciones || "Sin observaciones."}</p>
              </div>

              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button onClick={() => setSelectedRecord(null)} className="btn-secondary-custom" style={{ padding: "8px 16px" }}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="history-section-header" style={{ marginBottom: "20px" }}>
        <button onClick={() => setIsFormOpen(false)} className="btn-secondary-custom" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Volver al Historial</span>
        </button>
        <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--agave-green)" }}>
          Nuevo Registro de Maduración - Sección VII
        </h2>
      </div>

      <div className="cards-container-grid">
        {/* PASO 1: SELECCION DE LOTE Y DATOS GENERALES */}
        <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", padding: "24px" }}>
          <header className="detail-card-header" style={{ marginBottom: "20px" }}>
            <div className="detail-card-icon-wrapper-green" style={{ width: "42px", height: "42px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(46, 204, 113, 0.15)", color: "#2ecc71" }}>
              <LayersIcon style={{ fontSize: 24 }} />
            </div>
            <div className="detail-card-title-wrapper">
              <h2 style={{ fontSize: "18px", fontWeight: 800, margin: 0 }}>Paso 1: Identificación y Selección del Lote Origen</h2>
              <p style={{ margin: "2px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
                Seleccione el lote desde Graneles (Sección V) para auto-poblar los datos del mezcal.
              </p>
            </div>
          </header>

          <div className="species-grid">
            <div className="custom-input-group">
              <input
                type="date"
                id="madFechaMovimiento"
                name="fechaMovimiento"
                value={formData?.fechaMovimiento || today}
                onChange={handleChange}
                className="custom-input-field"
              />
              <label className="custom-input-label" htmlFor="madFechaMovimiento">Fecha del Movimiento</label>
            </div>

            <div className="custom-input-group">
              <select
                name="noLote"
                value={formData?.granelOrigenId ? `GRANEL:${formData.granelOrigenId}` : (formData?.noLote ? `GRANEL:${formData.noLote}` : "")}
                onChange={handleLoteSelect}
                className="custom-select-field"
                style={{ padding: "18px 16px 8px 16px", fontWeight: 600 }}
              >
                <option value="">-- Seleccionar Lote Origen Granel (Sección V) --</option>
                {(granelRecords || []).map((r, idx) => {
                  const entVol = parseFloat(r.entradaVolumen || r.invInicialVol || 0);
                  const salVol = parseFloat(r.salidaVolumen || 0);
                  const volDisp = r.invFinalVol !== undefined && r.invFinalVol !== "" ? parseFloat(r.invFinalVol) : Math.max(0, entVol - salVol);
                  return (
                    <option key={`g-${idx}`} value={`GRANEL:${r.id || r.noLote || r.idContenedor}`}>
                      📦 Granel Lote: {r.noLote || r.idContenedor} - {r.categoria} {r.clase} (Disponible: {volDisp} L)
                    </option>
                  );
                })}
              </select>
              <label className="custom-input-label">Nº Lote Origen (Granel)</label>
            </div>
          </div>

          {/* BADGE DE INFORMACION DE GRANEL SELECCIONADO */}
          {formData.granelOrigenId && (
            <div style={{ border: "1px solid rgba(46, 204, 113, 0.4)", padding: "14px 18px", borderRadius: "12px", marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", fontSize: "13px" }}>
              <div>
                <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block" }}>Lote Granel:</span>
                <strong style={{ color: "#2ecc71", fontSize: "15px" }}>{formData.noLote}</strong>
              </div>
              <div>
                <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block" }}>Volumen Disponible:</span>
                <strong style={{ color: "var(--agave-green)", fontSize: "15px" }}>{formData.granelVolDisponible} Litros</strong>
              </div>
              <div>
                <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block" }}>Graduación Alcohólica:</span>
                <strong style={{ color: "#f39c12", fontSize: "15px" }}>{formData.granelAlcGrad}% Alc. Vol.</strong>
              </div>
              <div>
                <span style={{ color: "var(--text-muted)", fontSize: "11px", display: "block" }}>Categoría / Clase:</span>
                <strong style={{ color: "#ffffff", fontSize: "14px" }}>{formData.categoria} ({formData.clase})</strong>
              </div>
            </div>
          )}

          {superaSaldoGranel && (
            <div style={{ background: "rgba(231, 76, 60, 0.15)", border: "1px solid rgba(231, 76, 60, 0.4)", padding: "12px 16px", borderRadius: "10px", marginTop: "12px", fontSize: "13.5px", color: "#e74c3c" }}>
              ⚠️ <strong>Atención:</strong> El volumen total a ingresar a maduración (<strong>{totalVolumenMadurar} L</strong>) supera el saldo disponible en Graneles (<strong>{formData.granelVolDisponible} L</strong>).
            </div>
          )}

          <div className="species-grid" style={{ marginTop: "20px" }}>
            <div className="custom-input-group">
              <select
                name="categoria"
                value={formData?.categoria || "Mezcal"}
                onChange={handleChange}
                className="custom-select-field"
                style={{ padding: "18px 16px 8px 16px" }}
              >
                <option value="Mezcal">Mezcal</option>
                <option value="Mezcal Artesanal">Mezcal Artesanal</option>
                <option value="Mezcal Ancestral">Mezcal Ancestral</option>
              </select>
              <label className="custom-input-label">Categoría</label>
            </div>

            <div className="custom-input-group">
              <input
                type="text"
                name="clase"
                value={formData?.clase || "Joven / Blanco"}
                onChange={handleChange}
                className="custom-input-field"
              />
              <label className="custom-input-label">Clase de Origen</label>
            </div>
          </div>

          <div className="species-grid">
            <div className="custom-input-group">
              <input
                type="text"
                name="ingredientes"
                value={formData?.ingredientes || ""}
                onChange={handleChange}
                className="custom-input-field"
              />
              <label className="custom-input-label">Ingrediente(s)</label>
            </div>

            <div className="custom-input-group">
              <input
                type="text"
                name="tipoMaguey"
                value={formData?.tipoMaguey || ""}
                onChange={handleChange}
                className="custom-input-field"
              />
              <label className="custom-input-label">Tipo de Maguey(es)</label>
            </div>

            <div className="custom-input-group">
              <input
                type="text"
                name="noAnalisis"
                value={formData?.noAnalisis || ""}
                onChange={handleChange}
                className="custom-input-field"
              />
              <label className="custom-input-label">Nº Análisis Fisicoquímico</label>
            </div>
          </div>
        </section>

        {/* PASO 2: RECIPIENTES DE MADURACIÓN DINÁMICOS */}
        <section className="detail-card detail-card-accent-left-copper" style={{ gridColumn: "1 / -1", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", padding: "24px" }}>
          <header className="detail-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div className="detail-card-icon-wrapper-copper" style={{ width: "42px", height: "42px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(210, 105, 30, 0.15)", color: "var(--copper)" }}>
                <ScienceIcon style={{ fontSize: 24 }} />
              </div>
              <div className="detail-card-title-wrapper">
                <h2 style={{ fontSize: "18px", fontWeight: 800, margin: 0 }}>Paso 2: Recipientes de Maduración & NOM-070</h2>
                <p style={{ margin: "2px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
                  Agregue recipientes (Barricas de Madera o Garrafas de Vidrio). La norma NOM-070 determinará automáticamente la Clase.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleAddRecipiente}
              className="btn-add-new"
              style={{ background: "rgba(46, 204, 113, 0.15)", color: "#2ecc71", border: "1px solid rgba(46, 204, 113, 0.4)", padding: "8px 16px", borderRadius: "10px" }}
            >
              <AddIcon style={{ fontSize: 18 }} />
              <span style={{ fontWeight: 700 }}>+ Agregar Recipiente</span>
            </button>
          </header>

          {recipientes.map((recItem, idx) => {
            const normaEval = evaluarNormaClase(
              recItem.tipoRecipiente,
              recItem.invInicialFecha || formData?.fechaMovimiento || today,
              recItem.salidaFecha || today
            );

            return (
              <div
                key={recItem.id || idx}
                style={{
                  background: "rgba(255,255,255,0.015)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "16px",
                  padding: "24px",
                  marginBottom: "28px"
                }}
              >
                {/* ENCABEZADO Y DATOS DE RECIPIENTE */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: "16px", marginBottom: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <LocalOfferIcon style={{ color: "var(--copper)", fontSize: 22 }} />
                    <h3 style={{ margin: 0, fontSize: "17px", color: "var(--agave-green)", fontWeight: 800 }}>
                      Recipiente #{idx + 1}
                    </h3>
                  </div>

                  {recipientes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipiente(recItem.id)}
                      style={{ background: "rgba(231, 76, 60, 0.15)", color: "#e74c3c", border: "1px solid rgba(231, 76, 60, 0.3)", borderRadius: "8px", padding: "6px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <DeleteIcon style={{ fontSize: 16 }} />
                      <span style={{ fontSize: "12px", fontWeight: 700 }}>Eliminar Recipiente</span>
                    </button>
                  )}
                </div>

                {/* FILA SUPERIOR: TIPO DE RECIPIENTE Y DETALLE / MARCA */}
                <div className="species-grid" style={{ marginBottom: "24px" }}>
                  <div className="custom-input-group">
                    <select
                      value={recItem.tipoRecipiente}
                      onChange={e => handleRecipienteChange(recItem.id, "tipoRecipiente", e.target.value)}
                      className="custom-select-field"
                      style={{ padding: "18px 16px 8px 16px", fontWeight: 600 }}
                    >
                      <option value="BARRICAS DE MADERA DE">BARRICAS DE MADERA DE</option>
                      <option value="PIPONES DE MADERA">PIPONES DE MADERA</option>
                      <option value="TONELES DE MADERA">TONELES DE MADERA</option>
                      <option value="GARRAFAS DE VIDRIO">GARRAFAS DE VIDRIO</option>
                      <option value="DAMIAJUANAS DE VIDRIO">DAMIAJUANAS DE VIDRIO</option>
                    </select>
                    <label className="custom-input-label">Tipo de Recipiente</label>
                  </div>

                  <div className="custom-input-group">
                    <input
                      type="text"
                      value={recItem.detalleRecipiente || ""}
                      onChange={e => handleRecipienteChange(recItem.id, "detalleRecipiente", e.target.value)}
                      className="custom-input-field"
                      placeholder="Ej. Roble Americano 200L / Garrafa Vidrio 20L"
                    />
                    <label className="custom-input-label">Detalle / marca recipiente</label>
                  </div>
                </div>

                {/* TARJETA 1: INVENTARIO INICIAL & ENTRADAS */}
                <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(210, 105, 30, 0.2)", borderRadius: "14px", padding: "20px", marginBottom: "24px" }}>
                  <header style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px" }}>
                    <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(210, 105, 30, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--copper)" }}>
                      <InventoryIcon style={{ fontSize: 20 }} />
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "var(--text-main)" }}>Inventario Inicial & Entradas</h4>
                      <p style={{ margin: "2px 0 0 0", color: "var(--text-muted)", fontSize: "12px" }}>Saldos de inicio y flujos de ingreso de mezcal</p>
                    </div>
                  </header>

                  {/* SUBSECCION: INVENTARIO INICIAL */}
                  <h5 style={{ fontSize: "12px", color: "var(--agave-green)", textTransform: "uppercase", letterSpacing: "0.5px", margin: "14px 0 10px 0", fontWeight: 800 }}>
                    INVENTARIO INICIAL
                  </h5>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                    <div className="custom-input-group">
                      <input
                        type="date"
                        value={recItem.invInicialFecha || formData?.fechaMovimiento || today}
                        onChange={e => handleRecipienteChange(recItem.id, "invInicialFecha", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">FECHA DE INGRESO</label>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.invInicialRecipientes || recItem.noRecipientes || "1"}
                        onChange={e => handleRecipienteChange(recItem.id, "invInicialRecipientes", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">Nº RECIPIENTES EX.</label>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.invInicialVolumen || ""}
                        onChange={e => handleRecipienteChange(recItem.id, "invInicialVolumen", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">VOLUMEN NETO (L)</label>
                      <span className="input-suffix">L</span>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        step="0.1"
                        value={recItem.invInicialAlc || formData?.granelAlcGrad || ""}
                        onChange={e => handleRecipienteChange(recItem.id, "invInicialAlc", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">% ALC. VOL.</label>
                      <span className="input-suffix">%</span>
                    </div>
                  </div>

                  {/* SUBSECCION: ENTRADAS */}
                  <h5 style={{ fontSize: "12px", color: "var(--agave-green)", textTransform: "uppercase", letterSpacing: "0.5px", margin: "16px 0 10px 0", fontWeight: 800 }}>
                    ENTRADAS
                  </h5>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
                    <div className="custom-input-group">
                      <input
                        type="text"
                        value={recItem.entradaFolio || ""}
                        onChange={e => handleRecipienteChange(recItem.id, "entradaFolio", e.target.value)}
                        className="custom-input-field"
                        placeholder="Ej. FOL-MAD-001"
                      />
                      <label className="custom-input-label">FOLIO DE INGRESO</label>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.entradaRecipientes || "0"}
                        onChange={e => handleRecipienteChange(recItem.id, "entradaRecipientes", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">Nº DE RECIPIENTES</label>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.entradaVolumen || "0"}
                        onChange={e => handleRecipienteChange(recItem.id, "entradaVolumen", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">VOLUMEN NETO (L)</label>
                      <span className="input-suffix">L</span>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        step="0.1"
                        value={recItem.entradaAlc || "0"}
                        onChange={e => handleRecipienteChange(recItem.id, "entradaAlc", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">% ALC. VOL.</label>
                      <span className="input-suffix">%</span>
                    </div>
                  </div>
                </div>

                {/* TARJETA 2: SALIDAS E INVENTARIO FINAL */}
                <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(46, 204, 113, 0.2)", borderRadius: "14px", padding: "20px" }}>
                  <header style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px" }}>
                    <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(46, 204, 113, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "#2ecc71" }}>
                      <ScienceIcon style={{ fontSize: 20 }} />
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "var(--text-main)" }}>Salidas e Inventario Final</h4>
                      <p style={{ margin: "2px 0 0 0", color: "var(--text-muted)", fontSize: "12px" }}>Volúmenes liberados, mermas de madera y saldo de cierre</p>
                    </div>
                  </header>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.salidaRecipientes || "0"}
                        onChange={e => handleRecipienteChange(recItem.id, "salidaRecipientes", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">Nº RECIPIENTES LIBERADOS</label>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.salidaVolumen || "0"}
                        onChange={e => handleRecipienteChange(recItem.id, "salidaVolumen", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">VOLUMEN NETO SALIDA (L)</label>
                      <span className="input-suffix">L</span>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        step="0.1"
                        value={recItem.salidaAlc || recItem.invInicialAlc || ""}
                        onChange={e => handleRecipienteChange(recItem.id, "salidaAlc", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">% ALC. VOL. SALIDA</label>
                      <span className="input-suffix">%</span>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="text"
                        readOnly
                        value={normaEval.clase}
                        className="custom-input-field"
                        style={{ fontWeight: 800, color: "#2ecc71", background: "rgba(46, 204, 113, 0.08)" }}
                      />
                      <label className="custom-input-label">CLASE OBTENER</label>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="text"
                        readOnly
                        value={normaEval.edad}
                        className="custom-input-field"
                        style={{ fontWeight: 800, color: "#f1c40f", background: "rgba(241, 196, 15, 0.08)" }}
                      />
                      <label className="custom-input-label">EDAD OBTENIDA</label>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.salidaMerma || "0"}
                        onChange={e => handleRecipienteChange(recItem.id, "salidaMerma", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">VOLUMEN DE MERMA (L)</label>
                      <span className="input-suffix" style={{ color: "var(--copper)", fontWeight: 700 }}>Merma L</span>
                    </div>
                  </div>

                  {/* SUBSECCION: INVENTARIO FINAL */}
                  <h5 style={{ fontSize: "12px", color: "var(--agave-green)", textTransform: "uppercase", letterSpacing: "0.5px", margin: "16px 0 10px 0", fontWeight: 800 }}>
                    INVENTARIO FINAL
                  </h5>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.invFinalRecipientes || "1"}
                        onChange={e => handleRecipienteChange(recItem.id, "invFinalRecipientes", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">RECIPIENTES EXISTENTES</label>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        value={recItem.invFinalVolumen || "0"}
                        onChange={e => handleRecipienteChange(recItem.id, "invFinalVolumen", e.target.value)}
                        className="custom-input-field"
                        style={{ fontWeight: 800, color: "var(--agave-green)" }}
                      />
                      <label className="custom-input-label">VOLUMEN NETO FINAL (L)</label>
                      <span className="input-suffix">L</span>
                    </div>

                    <div className="custom-input-group">
                      <input
                        type="number"
                        step="0.1"
                        value={recItem.invFinalAlc || recItem.invInicialAlc || ""}
                        onChange={e => handleRecipienteChange(recItem.id, "invFinalAlc", e.target.value)}
                        className="custom-input-field"
                      />
                      <label className="custom-input-label">% ALC. VOL. FINAL</label>
                      <span className="input-suffix">%</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* PASO 3: OBSERVACIONES Y FINALIZACION */}
        <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", padding: "24px" }}>
          <header className="detail-card-header" style={{ marginBottom: "16px" }}>
            <div className="detail-card-icon-wrapper-green" style={{ width: "42px", height: "42px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(46, 204, 113, 0.15)", color: "#2ecc71" }}>
              <SettingsIcon style={{ fontSize: 24 }} />
            </div>
            <div className="detail-card-title-wrapper">
              <h2 style={{ fontSize: "18px", fontWeight: 800, margin: 0 }}>Paso 3: Observaciones y Finalización</h2>
              <p style={{ margin: "2px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
                Comentarios sobre el estado de las barricas, tipos de tostado o trasvases oficiales
              </p>
            </div>
          </header>

          <div className="custom-input-group">
            <textarea
              id="madObservaciones"
              name="observaciones"
              value={formData?.observaciones || ""}
              onChange={handleChange}
              className="custom-input-field"
              style={{ height: "90px", resize: "none", padding: "14px" }}
              placeholder="Escriba aquí notas generales sobre la maduración..."
            />
            <label className="custom-input-label" htmlFor="madObservaciones">Observaciones Generales</label>
          </div>
        </section>
      </div>

      <div className="button-row" style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "14px" }}>
        <button type="button" className="btn-secondary-custom" onClick={() => setIsFormOpen(false)} style={{ padding: "12px 24px", borderRadius: "10px" }}>
          Volver
        </button>
        <button
          type="button"
          className="btn-primary-custom"
          disabled={superaSaldoGranel}
          onClick={() => handleSaveMaduracion("revision")}
          style={{ padding: "12px 28px", borderRadius: "10px", display: "flex", alignItems: "center", gap: 8, opacity: superaSaldoGranel ? 0.5 : 1, cursor: superaSaldoGranel ? "not-allowed" : "pointer" }}
        >
          <CheckCircleIcon style={{ fontSize: 20 }} />
          <span style={{ fontWeight: 800 }}>Registrar Sección VII</span>
        </button>
      </div>
    </div>
  );
};

export default MaduracionSection;
